import { AuthResponse, MccsRoles } from '@/@types/generated'
import Paper from '@/components/Atoms/Paper'
import { Collapsible } from '@/components/ui/collapsible'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
} from '@/components/ui/sidebar'
import { useAuth } from '@/hooks/useAuth'
import {
  ApplicationsFeature,
  CasesFeature,
  CredentialingFeature,
  EditQueueFeature,
  HelpDocumentsFeature,
  LicensingAgentsFeature,
  PaymentsFeature,
  TicketsFeature,
  UsersFeature,
} from '@/lib/features'
import { InsuranceFeature } from '@/lib/features/insurance'
import { InternalRoles } from '@/lib/utils'
import { hasRole } from '@/components/Atoms/Permissions'
import { RouteKey } from '@/routes'
import _ from 'lodash'
import {
  Building2Icon,
  CircleDollarSignIcon,
  CircleQuestionMark,
  ClipboardPenLineIcon,
  FileBadge2Icon,
  GavelIcon,
  IdCardLanyardIcon,
  MailWarningIcon,
  TicketsIcon,
  TruckIcon,
  UsersIcon,
  ListTodoIcon,
} from 'lucide-react'
import React from 'react'
import { Link, useLocation } from 'react-router'
import { v4 as uuid } from 'uuid'
import { CompaniesFeature } from '@/lib/features/companies'

type Route = {
  title: string
  url: RouteKey
  icon?: React.ComponentType<any>
  subRoutes?: Route[]
  isVisible: (user: AuthResponse | null) => boolean
}

type Separator = {
  type: 'separator'
  id: string
  isVisible: (user: AuthResponse | null) => boolean
}

const navigation = [
  {
    title: 'Companies',
    url: '/companies',
    icon: Building2Icon,
    isVisible: CompaniesFeature.isEnabled,
    subRoutes: [
      {
        title: 'VIN Lookup',
        url: '/companies/vin-lookup',
        isVisible: CompaniesFeature.VinLookup.isEnabled,
      },
    ],
  },
  {
    title: 'Credentialing',
    url: '/credentialing',
    icon: FileBadge2Icon,
    isVisible: CredentialingFeature.isEnabled,
    subRoutes: [
      {
        title: 'Chameleon Carriers',
        url: '/credentialing/chameleon-carriers',
        isVisible: InsuranceFeature.isEnabled,
      },
      {
        title: 'Vehicle Review',
        url: '/credentialing/vehicle-review',
        isVisible: CredentialingFeature.isEnabled,
      },
    ],
  },
  {
    title: 'Edit Queue',
    url: '/edit-queue',
    icon: ListTodoIcon,
    isVisible: EditQueueFeature.isEnabled,
  },
  {
    title: 'Bounced Emails',
    url: '/bounced-emails',
    icon: MailWarningIcon,
    isVisible: CredentialingFeature.isEnabled,
  },
  {
    title: 'Cases',
    url: '/cases',
    icon: GavelIcon,
    isVisible: CasesFeature.isEnabled,
    subRoutes: [
      {
        title: 'NODD Queue',
        url: '/cases/nodd-queue',
        isVisible: CasesFeature.isEnabled,
      },
      {
        title: 'Final Order Queue',
        url: '/cases/final-order-queue',
        isVisible: CasesFeature.isEnabled,
      },
    ],
  },
  {
    title: 'Insurance',
    url: '/insurance/insurers',
    icon: UsersIcon,
    isVisible: (user) =>
      InsuranceFeature.isEnabled(user) || hasRole(InternalRoles, user),
  },
  {
    title: 'Licensing Agencies',
    url: '/licensing-agencies',
    icon: IdCardLanyardIcon,
    isVisible: LicensingAgentsFeature.isEnabled,
  },
  {
    title: 'Payments',
    url: '/payments',
    icon: CircleDollarSignIcon,
    isVisible: PaymentsFeature.isEnabled,
  },
  {
    title: 'Tickets',
    url: '/tickets',
    icon: TicketsIcon,
    isVisible: TicketsFeature.isEnabled,
  },
  {
    title: 'Users',
    url: '/users',
    icon: UsersIcon,
    isVisible: UsersFeature.isEnabled,
  },
  { type: 'separator', id: uuid(), isVisible: () => true },
  {
    title: 'Applications',
    url: '/applications',
    icon: ClipboardPenLineIcon,
    isVisible: ApplicationsFeature.isEnabled,
  },
  {
    title: 'Truckstop',
    url: '/truckstop',
    icon: TruckIcon,
    isVisible: () => true,
  },
  {
    type: 'separator',
    id: uuid(),
    isVisible: HelpDocumentsFeature.isEnabled,
  },
  {
    title: 'Help',
    url: '/help-documents',
    icon: CircleQuestionMark,
    isVisible: HelpDocumentsFeature.isEnabled,
  },
] satisfies (Route | Separator)[]

const MccsSidebar = () => {
  // Hooks
  const { pathname } = useLocation()
  const { user } = useAuth()

  // Determine sort order of the navigation based on user role
  const hasRole = (roles: MccsRoles[]) =>
    roles.some((role) => user?.roles?.includes(role))
  const sortPaths: RouteKey[] = hasRole(LicensingAgentsFeature.Roles)
    ? // Licensing Agencies sort override
      ['/licensing-agencies']
    : []

  const visibleSubRoutes = (subRoutes?: Route[]) =>
    (subRoutes ?? []).filter(({ isVisible }) => isVisible(user))

  return (
    <Sidebar variant="inset" collapsible="icon" className="relative">
      <Paper>
        <SidebarContent>
          <SidebarGroup>
            <SidebarMenu>
              {navigation
                // Only show navigation items accessible to the user
                .filter(({ isVisible }) => isVisible(user))
                // Sort by the sortPaths order, if specified, otherwise default sort
                .sort((a, b) => {
                  const aIndex = sortPaths.indexOf(a.url!)
                  const bIndex = sortPaths.indexOf(b.url!)
                  if (aIndex === -1 && bIndex === -1) return 0
                  if (aIndex === -1) return 1
                  if (bIndex === -1) return -1
                  return aIndex - bIndex
                })
                .map((item) =>
                  item.type === 'separator' ? (
                    <li key={item.id}>
                      <SidebarSeparator className="ml-0" />
                    </li>
                  ) : (
                    <Collapsible
                      key={item.title}
                      asChild
                      className="group/collapsible"
                    >
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          isActive={pathname.startsWith(item.url)}
                          asChild
                        >
                          <Link
                            to={item.url}
                            className="font-medium whitespace-nowrap"
                          >
                            {item.icon && <item.icon />}
                            {item.title}
                          </Link>
                        </SidebarMenuButton>

                        {_.size(visibleSubRoutes(item.subRoutes)) > 0 && (
                          <SidebarMenuSub>
                            {visibleSubRoutes(item.subRoutes).map((subItem) => (
                              <SidebarMenuSubItem key={subItem.url}>
                                <SidebarMenuSubButton
                                  isActive={
                                    pathname.startsWith(item.url) &&
                                    pathname.includes(subItem.url)
                                  }
                                  asChild
                                >
                                  <Link
                                    to={subItem.url}
                                    className="font-medium"
                                  >
                                    {subItem.title}
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        )}
                      </SidebarMenuItem>
                    </Collapsible>
                  )
                )}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>
      </Paper>
    </Sidebar>
  )
}

export default MccsSidebar
